import type {
  DragConstraint,
  DragUpdateOptions,
  DragUpdateResult,
  SerializableVector3,
  SnapAnchor,
} from "@project/emerge-types";
import { Html } from "@react-three/drei";
import type { ThreeEvent } from "@react-three/fiber";
import { useThree } from "@react-three/fiber";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BufferGeometry,
  type Euler,
  Float32BufferAttribute,
  type Group,
  Line3,
  LineBasicMaterial,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
} from "three";
import { useDragStore } from "../stores/dragStore";
import { Arrow3D } from "./Arrow3D";
import {
  DragGuideLines,
  type GuideAxis,
  type GuideSegment,
} from "./DragGuideLines";
import { DragHighlightGlow } from "./DragHighlightGlow";
import {
  canAxisCenterSnapStayInBounds,
  DEFAULT_DRAG_GRID_STEP_MM,
  dragGridStepFromModifiers,
  projectOntoSurface,
  projectOntoSurfaceAreas,
  type ResolvedSurfaceArea,
  surfaceRectSegments,
} from "./surfaceDrag";

/** Distance threshold in world units for snapping to anchors and center lines */
const SNAP_THRESHOLD = 15;

/** Max pointer travel in screen pixels for a press to count as a click, not a drag */
const CLICK_MOVE_THRESHOLD_PX = 2;

/**
 * Squared world-distance tolerance (mm²) for deciding that an authoritative
 * build matches the committed final drag position during reconcile. Generous
 * enough to absorb sub-mm build rounding, far below the cursor-distance gap of
 * any stale intermediate build still in flight after pointer-up.
 */
const RECONCILE_MATCH_EPSILON_SQ = 4;

/**
 * Safety fallback (ms): if no build ever commits within
 * `RECONCILE_MATCH_EPSILON_SQ` of the final drag position (e.g. the authoritative
 * value is clamped server-side), drop the optimistic position anyway so the part
 * cannot stay frozen and deaf to future prop updates.
 */
const RECONCILE_TIMEOUT_MS = 1000;

/**
 * Module-level active drag session.
 *
 * A drag is driven by window/canvas pointer listeners, but the `DraggablePart`
 * that started it can be unmounted and immediately remounted mid-drag when its
 * React subtree is transiently rebuilt — an incremental product build can
 * briefly drop and re-add a part (observed for roller parts). If the listeners
 * and drag math lived only on the component, that remount would silently kill
 * the drag ("the part suddenly stops following the mouse").
 *
 * We therefore own the listeners at module scope and snapshot the drag math
 * here, so a freshly mounted instance of the *same* part can adopt the in-flight
 * session and keep dragging without interruption. The listeners also outlive the
 * component, so even if the part is removed for good, the user's pointer-up
 * still ends the drag (the stale handler closure is still callable).
 */
interface DragSessionSnapshot {
  plane: Plane | null;
  offset: Vector3 | null;
  initialPosition: Vector3 | null;
  pointerDownScreen: { x: number; y: number } | null;
  pointerMoved: boolean;
  lastDragPosition: Vector3 | null;
}

function getActiveDragMoveContext(
  isDragging: boolean,
  plane: Plane | null,
  offset: Vector3 | null,
): { plane: Plane; offset: Vector3 } | null {
  if (!isDragging || !plane || !offset) {
    return null;
  }
  return { plane, offset };
}

function markPointerMovedPastClickThreshold(
  event: PointerEvent,
  downScreen: { x: number; y: number } | null,
): boolean {
  if (!downScreen) {
    return false;
  }
  return (
    Math.hypot(event.clientX - downScreen.x, event.clientY - downScreen.y) >=
    CLICK_MOVE_THRESHOLD_PX
  );
}

interface DragSession {
  partId: string;
  /** Points at the currently owning instance's latest move/up handlers. */
  handlers: {
    onMove: (event: PointerEvent) => void;
    onUp: (event: PointerEvent) => void;
  };
  /** Drag math so a remounted instance can resume seamlessly. */
  snapshot: DragSessionSnapshot;
  /** Re-enable camera controls etc.; runs exactly once when the drag ends. */
  cleanup: (() => void) | null;
  /** Removes the window/canvas listeners. Called once when the drag truly ends. */
  detach: () => void;
}

let activeDragSession: DragSession | null = null;

/**
 * Single-owner control of the shared canvas cursor.
 *
 * The cursor lives on the one `<canvas>`, but every `DraggablePart` wants to set
 * it (each on hover / drag). Per-instance snapshot-and-restore races across
 * instances: on a hover hand-off, the next part snapshots the cursor while it is
 * still the previous part's override (e.g. "move") and later "restores" that
 * stale value forever — the stuck-cursor bug. Instead we track a single owner:
 * a part sets the cursor only while it owns it, and a release resets the DOM
 * (clearing our inline override so the canvas reverts to its default cursor)
 * *only* when the caller is still the owner. A stale/late instance can therefore
 * neither clobber the current owner's cursor nor resurrect an old value.
 */
let cursorOwnerPartId: string | null = null;

function acquireCanvasCursor(
  el: HTMLElement,
  partId: string,
  cursor: string,
): void {
  cursorOwnerPartId = partId;
  el.style.cursor = cursor;
}

function releaseCanvasCursor(el: HTMLElement, partId: string): void {
  if (cursorOwnerPartId === partId) {
    cursorOwnerPartId = null;
    el.style.cursor = "";
  }
}

/** Type for React Three Fiber components that accept userData */
type WithUserData = {
  userData?: { isDragHelper?: boolean };
};

/** Display values returned from drag update */
export type DragDisplayValues = DragUpdateResult<unknown>["displayValues"];

/** Result returned from onDrag during pointer move */
export type DragOnMoveResult = Pick<
  DragUpdateResult<unknown>,
  "displayValues" | "snappedPosition"
>;

/** Props for the DraggablePart component */
export interface DraggablePartProps {
  /** Unique identifier for this part (used for drag state tracking) */
  partId: string;
  /** The part's current world position */
  position: Vector3;
  /** The part's rotation (Euler angles) */
  rotation?: Euler;
  /** The drag constraint defining movement limits */
  constraint: DragConstraint;
  /** Called continuously during drag with the constrained position. */
  onDrag?: (
    position: Vector3,
    options?: DragUpdateOptions,
  ) => DragOnMoveResult | undefined;
  /** Called when drag ends with the final position (may be snapped) */
  onDragEnd?: (
    position: Vector3,
    snappedAnchor?: SnapAnchor,
    options?: DragUpdateOptions,
  ) => void;
  /** Called when drag starts - return cleanup function to re-enable controls */
  onDragStart?: () => (() => void) | undefined;
  /** Called on a modifier-click that did not turn into a drag (open part menu) */
  onActivate?: () => void;
  /** Children to render (the actual part) */
  children: React.ReactNode;
  /** Whether dragging is enabled (default: true) */
  enabled?: boolean;
  /** Whether to show hover highlight (default: true) */
  showHoverHighlight?: boolean;
  /** Show debug visuals for constraint bounds (default: false) */
  debug?: boolean;
  /** If true, do not update local state during drag; rely on parent to update position prop (default: false) */
  controlled?: boolean;
}

/**
 * Converts a SerializableVector3 tuple to a Three.js Vector3.
 */
function toVector3(v: SerializableVector3): Vector3 {
  return new Vector3(v[0], v[1], v[2]);
}

/**
 * Projects a point onto an edge constraint (1D line segment).
 * The edge is defined by bounds.min to bounds.max.
 */
function projectOntoEdge(
  point: Vector3,
  bounds: { min: Vector3; max: Vector3 },
): Vector3 {
  // Create line segment from bounds
  const line = new Line3(bounds.min, bounds.max);
  const projected = new Vector3();
  // true = clamp to line segment (not infinite line)
  line.closestPointToPoint(point, true, projected);
  return projected;
}

/**
 * A diagonal snap line resolved to Three.js objects (segment endpoints).
 */
interface ResolvedDiagonalSnap {
  start: Vector3;
  end: Vector3;
  key: string;
  label?: string;
}

/**
 * Projects a point onto a (clamped) line segment from `start` to `end`.
 */
function projectOntoSegment(
  point: Vector3,
  start: Vector3,
  end: Vector3,
): Vector3 {
  const line = new Line3(start, end);
  const projected = new Vector3();
  line.closestPointToPoint(point, true, projected);
  return projected;
}

/**
 * Result of resolving where a surface-constrained point snaps to.
 */
interface SurfaceSnapResult {
  /** The snapped in-plane position. */
  position: Vector3;
  /** Per-axis center-snap state (parallel to `axes`). */
  axisSnapState: boolean[];
  /** Per-diagonal snap state (parallel to `diagonals`). */
  diagonalSnapState: boolean[];
}

/**
 * Resolves surface snapping for a constrained point. A nearby diagonal (45°)
 * line takes precedence and fully fixes the in-plane position; otherwise each
 * in-plane axis snaps to the center independently.
 */
interface ActiveSurfaceAreaSnapContext {
  planeNormal: Vector3;
  bounds: { min: Vector3; max: Vector3 };
}

function resolveSurfaceSnap(
  point: Vector3,
  axes: Vector3[],
  center: Vector3,
  diagonals: ResolvedDiagonalSnap[],
  threshold: number,
  activeArea?: ActiveSurfaceAreaSnapContext,
): SurfaceSnapResult {
  let bestIdx = -1;
  let bestDist = threshold;
  let bestPoint: Vector3 | null = null;
  diagonals.forEach((snap, i) => {
    const projected = projectOntoSegment(point, snap.start, snap.end);
    const dist = point.distanceTo(projected);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
      bestPoint = projected;
    }
  });

  if (bestIdx >= 0 && bestPoint) {
    const position = activeArea
      ? projectOntoSurface(bestPoint, activeArea.planeNormal, activeArea.bounds)
      : bestPoint;
    return {
      position,
      axisSnapState: axes.map(() => false),
      diagonalSnapState: diagonals.map((_, i) => i === bestIdx),
    };
  }

  const snapped = point.clone();
  const axisSnapState = axes.map((axis) => {
    const distToCenter = snapped.clone().sub(center).dot(axis);
    const withinThreshold = Math.abs(distToCenter) < threshold;
    const staysInBounds =
      !activeArea ||
      canAxisCenterSnapStayInBounds(
        snapped,
        axis,
        center,
        activeArea.planeNormal,
        activeArea.bounds,
      );
    const isSnapped = withinThreshold && staysInBounds;
    if (isSnapped) {
      snapped.addScaledVector(axis, -distToCenter);
    }
    return isSnapped;
  });
  if (activeArea) {
    snapped.copy(
      projectOntoSurface(snapped, activeArea.planeNormal, activeArea.bounds),
    );
  }
  return {
    position: snapped,
    axisSnapState,
    diagonalSnapState: diagonals.map(() => false),
  };
}

/**
 * Updates the in-flight drag session snapshot so a mid-drag remount of the part
 * resumes from the latest position/threshold state. No-op when no session is
 * active (e.g. legacy uncontrolled usage outside the session flow).
 */
function persistDragSessionSnapshot(
  partId: string,
  finalPos: Vector3,
  pointerMoved: boolean,
): void {
  if (activeDragSession?.partId === partId) {
    activeDragSession.snapshot.lastDragPosition = finalPos.clone();
    activeDragSession.snapshot.pointerMoved = pointerMoved;
  }
}

function booleanArraysEqual(a: boolean[], b: boolean[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

function teardownDragSession(session: DragSession): void {
  session.detach();
  session.cleanup?.();
}

function attachDragPointerListeners(domElement: HTMLElement): () => void {
  const moveListener = (e: PointerEvent) =>
    activeDragSession?.handlers.onMove(e);
  const endListener = (e: PointerEvent) => activeDragSession?.handlers.onUp(e);
  globalThis.addEventListener("pointermove", moveListener);
  globalThis.addEventListener("pointerup", endListener);
  globalThis.addEventListener("pointercancel", endListener);
  domElement.addEventListener("pointermove", moveListener);
  domElement.addEventListener("pointerup", endListener);
  domElement.addEventListener("pointercancel", endListener);
  return () => {
    globalThis.removeEventListener("pointermove", moveListener);
    globalThis.removeEventListener("pointerup", endListener);
    globalThis.removeEventListener("pointercancel", endListener);
    domElement.removeEventListener("pointermove", moveListener);
    domElement.removeEventListener("pointerup", endListener);
    domElement.removeEventListener("pointercancel", endListener);
  };
}

/**
 * Finds the closest snap anchor within the threshold distance.
 */
function findSnapAnchor(
  position: Vector3,
  anchors: SnapAnchor[],
  threshold: number,
): SnapAnchor | undefined {
  let closest: SnapAnchor | undefined;
  let closestDistance = threshold;

  for (const anchor of anchors) {
    const anchorPos = toVector3(anchor.position);
    const distance = position.distanceTo(anchorPos);
    if (distance < closestDistance) {
      closest = anchor;
      closestDistance = distance;
    }
  }

  return closest;
}

interface ConstrainedDragProjection {
  constrainedPosition: Vector3;
  activeCenter: Vector3;
  activeAreaIndex: number | null;
}

function projectConstrainedDragPosition(
  newLocalPosition: Vector3,
  args: {
    constraintType: DragConstraint["type"];
    planeNormal: Vector3 | undefined;
    surfaceAreas: ResolvedSurfaceArea[];
    perAreaCenterSnap: boolean;
    defaultCenter: Vector3;
    projectPoint: (point: Vector3) => Vector3;
  },
): ConstrainedDragProjection {
  const {
    constraintType,
    planeNormal,
    surfaceAreas,
    perAreaCenterSnap,
    defaultCenter,
    projectPoint,
  } = args;

  if (constraintType === "surface" && planeNormal && surfaceAreas.length > 0) {
    const projection = projectOntoSurfaceAreas(
      newLocalPosition,
      planeNormal,
      surfaceAreas,
    );
    let activeCenter = defaultCenter;
    const areaCenter = surfaceAreas[projection.index]?.center;
    if (perAreaCenterSnap && areaCenter) {
      activeCenter = areaCenter;
    }
    return {
      constrainedPosition: projection.position,
      activeCenter,
      activeAreaIndex: projection.index,
    };
  }

  return {
    constrainedPosition: projectPoint(newLocalPosition),
    activeCenter: defaultCenter,
    activeAreaIndex: null,
  };
}

interface DragSnapOutcome {
  finalPos: Vector3;
  snapAnchor?: SnapAnchor;
  surfaceSnap?: SurfaceSnapResult;
}

interface DragSnapContext {
  constraint: Pick<DragConstraint, "type" | "snapAnchors" | "visualOffset">;
  surfaceAxes: Vector3[];
  activeCenter: Vector3;
  diagonalSnaps: ResolvedDiagonalSnap[];
  threshold: number;
  activeAreaIndex: number | null;
  surfaceAreas: ResolvedSurfaceArea[];
  planeNormal: Vector3 | undefined;
}

function resolveDragSnapOutcome(
  constrainedPosition: Vector3,
  ctx: DragSnapContext,
): DragSnapOutcome {
  const {
    constraint,
    surfaceAxes,
    activeCenter,
    diagonalSnaps,
    threshold,
    activeAreaIndex,
    surfaceAreas,
    planeNormal,
  } = ctx;
  if (constraint.type === "surface" && surfaceAxes.length > 0) {
    const activeBounds =
      activeAreaIndex != null && activeAreaIndex >= 0
        ? surfaceAreas[activeAreaIndex]?.bounds
        : undefined;
    const activeArea =
      activeBounds && planeNormal
        ? { planeNormal, bounds: activeBounds }
        : undefined;
    const surfaceSnap = resolveSurfaceSnap(
      constrainedPosition,
      surfaceAxes,
      activeCenter,
      diagonalSnaps,
      threshold,
      activeArea,
    );
    return { finalPos: surfaceSnap.position, surfaceSnap };
  }

  if (constraint.type === "edge") {
    const partCenterPos = constraint.visualOffset
      ? constrainedPosition.clone().add(toVector3(constraint.visualOffset))
      : constrainedPosition;
    const snapAnchor = findSnapAnchor(
      partCenterPos,
      constraint.snapAnchors,
      threshold,
    );
    return {
      finalPos: snapAnchor ? toVector3(snapAnchor.position) : partCenterPos,
      snapAnchor,
    };
  }

  const snapAnchor = findSnapAnchor(
    constrainedPosition,
    constraint.snapAnchors,
    threshold,
  );
  return {
    finalPos: snapAnchor ? toVector3(snapAnchor.position) : constrainedPosition,
    snapAnchor,
  };
}

/**
 * Component that wraps a part and makes it draggable according to its constraint.
 *
 * Features:
 * - Projects pointer movement onto edge (1D) or surface (2D) constraints
 * - Respects movement bounds
 * - Snaps to anchor points when within threshold
 * - Shows visual guides during drag
 */
export const DraggablePart: React.FC<DraggablePartProps> = ({
  partId,
  position,
  rotation,
  constraint,
  onDrag,
  onDragEnd,
  onDragStart,
  onActivate,
  children,
  enabled = true,
  showHoverHighlight = true,
  debug = false,
  controlled = false,
}) => {
  // Use global drag store to track which part is being dragged
  // Use selector to only re-render when this specific part's drag state changes
  const draggingPartId = useDragStore((state) => state.draggingPartId);
  const isDragActive = useDragStore((state) => state.isDragActive);
  const startDrag = useDragStore((state) => state.startDrag);
  const endDrag = useDragStore((state) => state.endDrag);
  const { camera, gl } = useThree();
  const groupRef = useRef<Group>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // Whether the drag modifier (⌘/Ctrl) is currently held while hovering. Gates
  // the drag-mode affordances (movement outline, arrow cross, move cursor) so
  // they appear only once the user is actually able to drag — not on bare hover.
  const [modifierHeld, setModifierHeld] = useState(false);
  const [dragPosition, setDragPosition] = useState<Vector3 | null>(null);
  const [hoveredAnchor, setHoveredAnchor] = useState<SnapAnchor | undefined>();
  // Which surface center lines (horizontal/vertical middle) the part is
  // currently snapped to, by index into `surfaceAxes`. Drives guide highlight.
  const [snappedAxes, setSnappedAxes] = useState<boolean[]>([]);
  // Which diagonal snap lines the part is currently snapped to, by index into
  // `diagonalSnaps`. Drives the diagonal guide highlight.
  const [snappedDiagonals, setSnappedDiagonals] = useState<boolean[]>([]);
  // Index into `surfaceAreas` of the panel the part is currently over. Drives
  // which per-panel center guide line is highlighted as snapped (per-area mode).
  const [activeAreaIndex, setActiveAreaIndex] = useState(0);
  const [displayValues, setDisplayValues] =
    useState<DragDisplayValues>(undefined);
  const isDraggingRef = useRef(false); // Ref for reliable access in event handlers
  const didDragRef = useRef(false); // Track if a drag occurred (to suppress click)
  const pointerDownScreenRef = useRef<{ x: number; y: number } | null>(null); // For click vs drag detection
  const pointerMovedRef = useRef(false); // True once the pointer travelled past the click threshold during this press
  const lastGridStepMmRef = useRef(DEFAULT_DRAG_GRID_STEP_MM);
  /** Last constrained cursor position; used for onDragEnd when rebuild-driven. */
  const lastDragFinalPosRef = useRef<Vector3 | null>(null);
  const initialPositionRef = useRef<Vector3 | null>(null);
  const dragPlaneRef = useRef<Plane | null>(null);
  const offsetRef = useRef<Vector3 | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const raycasterRef = useRef(new Raycaster());
  const hoveredAnchorRef = useRef<SnapAnchor | undefined>(undefined);
  // Controlled mode only: set on pointer up to keep the optimistic drag
  // position until the authoritative rebuild commits a new `position` prop.
  const pendingReconcileRef = useRef(false);
  // The committed final drag position captured on pointer up. Reconcile waits
  // for an authoritative build that matches this (not just any prop change), so
  // a stale in-flight intermediate build cannot snap the part to the wrong spot.
  const finalDragPositionRef = useRef<Vector3 | null>(null);
  const reconcileTimeoutRef = useRef<ReturnType<
    typeof globalThis.setTimeout
  > | null>(null);

  // Convert constraint bounds to Vector3
  const bounds = useMemo(
    () => ({
      min: toVector3(constraint.bounds.min),
      max: toVector3(constraint.bounds.max),
    }),
    [constraint.bounds],
  );

  const planeNormal = useMemo(
    () =>
      constraint.planeNormal
        ? toVector3(constraint.planeNormal).normalize()
        : undefined,
    [constraint.planeNormal],
  );

  // Center of the movement area (the surface/edge middle). Prefer the
  // buildstep's "Center" anchor (the visual rect/edge center) and fall back to
  // the bounds center, so the guide lines line up with the geometry.
  const center = useMemo(() => {
    const centerAnchor = constraint.snapAnchors.find(
      (a) => a.label === "Center",
    );
    if (centerAnchor) return toVector3(centerAnchor.position);
    return bounds.min.clone().add(bounds.max).multiplyScalar(0.5);
  }, [constraint.snapAnchors, bounds]);

  // For a surface, the two world axes that lie in the plane (the in-plane
  // horizontal/vertical directions). The plane normal is axis-aligned, so the
  // in-plane axes are simply the other two world axes.
  const surfaceAxes = useMemo(() => {
    if (constraint.type !== "surface" || !planeNormal) return [];
    return [
      new Vector3(1, 0, 0),
      new Vector3(0, 1, 0),
      new Vector3(0, 0, 1),
    ].filter((axis) => Math.abs(planeNormal.dot(axis)) < 0.9);
  }, [constraint.type, planeNormal]);

  // Multiple valid panels (drag areas) of the side, when the constraint exposes
  // them. Empty for single-panel/edge constraints (falls back to `bounds`).
  const surfaceAreas = useMemo<ResolvedSurfaceArea[]>(() => {
    if (constraint.type !== "surface" || !constraint.areas) return [];
    return constraint.areas.map((area) => ({
      bounds: {
        min: toVector3(area.bounds.min),
        max: toVector3(area.bounds.max),
      },
      center: area.center ? toVector3(area.center) : undefined,
    }));
  }, [constraint.type, constraint.areas]);

  // Per-area center snapping ("Relativ zu Korpus"): snap to each panel's own
  // center and draw a guide line per panel. Otherwise ("Relativ zu Case") snap
  // to the single global center with one guide line across the whole side.
  const perAreaCenterSnap =
    constraint.type === "surface" &&
    constraint.perAreaCenterSnap === true &&
    surfaceAreas.length > 0;

  // Global center guide lines spanning the (union) bounds along each in-plane
  // axis, marked as snapped for highlighting. Each line passes through the
  // single surface center. Used for "Relativ zu Case" (global snap).
  //
  // A line drawn *along* axis i is the one the part lies on when its position on
  // the *perpendicular* in-plane axis is centered. `snappedAxes[i]` tracks the
  // center-snap of axis i, so the line along axis i must read the perpendicular
  // axis's snap state (the two in-plane axes, index `len-1-i`) — otherwise the
  // horizontal/vertical highlight is swapped.
  const guideAxes = useMemo<GuideAxis[]>(() => {
    const extent = bounds.max.clone().sub(bounds.min);
    return surfaceAxes.map((dir, i) => ({
      dir,
      halfLength: Math.abs(extent.dot(dir)) / 2,
      snapped: snappedAxes[surfaceAxes.length - 1 - i] ?? false,
      key: `${dir.x}_${dir.y}_${dir.z}`,
    }));
  }, [surfaceAxes, bounds, snappedAxes]);

  // Per-panel center guide lines for "Relativ zu Korpus": one center cross per
  // drag area, spanning that panel's own bounds through its own center. Only the
  // panel the part is currently over (`activeAreaIndex`) shows the snapped
  // highlight; the rest stay idle so every panel's center is still indicated.
  const areaGuides = useMemo<
    Array<{ key: string; center: Vector3; axes: GuideAxis[] }>
  >(() => {
    if (!perAreaCenterSnap) return [];
    return surfaceAreas.map((area, areaIdx) => {
      const areaCenter =
        area.center ??
        area.bounds.min.clone().add(area.bounds.max).multiplyScalar(0.5);
      const extent = area.bounds.max.clone().sub(area.bounds.min);
      const isActive = areaIdx === activeAreaIndex;
      // See `guideAxes`: the line along axis i highlights with the perpendicular
      // axis's center-snap state (`len-1-i`).
      const axes = surfaceAxes.map((dir, axisIdx) => ({
        dir,
        halfLength: Math.abs(extent.dot(dir)) / 2,
        snapped:
          isActive && (snappedAxes[surfaceAxes.length - 1 - axisIdx] ?? false),
        key: `${areaIdx}_${dir.x}_${dir.y}_${dir.z}`,
      }));
      return {
        key: `area_${areaIdx}`,
        center: areaCenter,
        axes,
      };
    });
  }, [
    perAreaCenterSnap,
    surfaceAreas,
    surfaceAxes,
    snappedAxes,
    activeAreaIndex,
  ]);

  // Diagonal (45°) snap lines for surface drags, resolved to segment endpoints.
  // A single-panel roller snaps onto its corner diagonal to keep equal margins.
  const diagonalSnaps = useMemo<ResolvedDiagonalSnap[]>(() => {
    if (constraint.type !== "surface" || !constraint.diagonalSnaps) return [];
    return constraint.diagonalSnaps.map((snap, i) => {
      const start = toVector3(snap.origin);
      const dir = toVector3(snap.direction).normalize();
      const end = start.clone().addScaledVector(dir, snap.length);
      return {
        start,
        end,
        key: snap.label ? `${snap.label}_${i}` : `diagonal_${i}`,
        label: snap.label,
      };
    });
  }, [constraint.type, constraint.diagonalSnaps]);

  // Diagonal guide segments spanning each snap line, highlighted when snapped.
  const guideSegments = useMemo<GuideSegment[]>(
    () =>
      diagonalSnaps.map((snap, i) => ({
        start: snap.start,
        end: snap.end,
        snapped: snappedDiagonals[i] ?? false,
        key: snap.key,
      })),
    [diagonalSnaps, snappedDiagonals],
  );

  // Optimistic / snap-display parts mirror drag locally. Rebuild-driven parts
  // (corner dimension resizes) keep the built `position` prop — the mesh stays
  // on the moving case corner while settings rebuild each frame.
  const useOptimisticDrag = !controlled || constraint.optimistic === true;
  const trackLocalDragPosition =
    constraint.optimistic === true || constraint.snapDisplayDuringDrag === true;

  /**
   * Projects a world point onto the constraint.
   */
  const projectPoint = useCallback(
    (worldPoint: Vector3): Vector3 => {
      if (constraint.type === "edge") {
        return projectOntoEdge(worldPoint, bounds);
      } else if (constraint.type === "surface" && planeNormal) {
        return projectOntoSurface(worldPoint, planeNormal, bounds);
      }
      // Fallback: clamp to bounds
      return worldPoint.clone().clamp(bounds.min, bounds.max);
    },
    [constraint.type, planeNormal, bounds],
  );

  /**
   * Raycast from screen position onto a plane.
   */
  const raycastOntoPlane = useCallback(
    (clientX: number, clientY: number, plane: Plane): Vector3 | null => {
      const rect = gl.domElement.getBoundingClientRect();
      const ndc = new Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycasterRef.current.setFromCamera(ndc, camera);
      const ray = raycasterRef.current.ray;
      const intersection = new Vector3();
      return ray.intersectPlane(plane, intersection);
    },
    [camera, gl.domElement],
  );

  /** Abandon pending optimistic reconcile (prior controlled drag still settling). */
  const clearPendingReconcile = useCallback(() => {
    if (reconcileTimeoutRef.current !== null) {
      globalThis.clearTimeout(reconcileTimeoutRef.current);
      reconcileTimeoutRef.current = null;
    }
    pendingReconcileRef.current = false;
    finalDragPositionRef.current = null;
    setDragPosition(null);
  }, []);

  /**
   * Global pointer move handler for drag (attached to window during drag).
   * Uses refs instead of state to avoid stale closure issues.
   */
  const handleGlobalPointerMove = useCallback(
    (event: PointerEvent) => {
      const dragMove = getActiveDragMoveContext(
        isDraggingRef.current,
        dragPlaneRef.current,
        offsetRef.current,
      );
      if (!dragMove) {
        return;
      }
      const { plane: dragPlane, offset } = dragMove;

      // Track whether the pointer ever travelled past the click threshold during
      // this press. Comparing only down-vs-up positions misclassifies a drag that
      // returns to its start as a click.
      const crossedThreshold = markPointerMovedPastClickThreshold(
        event,
        pointerDownScreenRef.current,
      );
      if (crossedThreshold && !pointerMovedRef.current) {
        // Starting a real drag — drop any pending optimistic reconcile from a
        // prior drag. Defer until threshold so a modifier-click to open the part
        // menu right after a drag does not snap back to a stale position prop.
        clearPendingReconcile();
      }
      if (crossedThreshold) {
        pointerMovedRef.current = true;
      }

      // Activate-only parts (e.g. the roller board panel) never move: they only
      // distinguish click from drag so a modifier-click can open their popup.
      // Skip all projection/snapping/onDrag work.
      if (constraint.activateOnly) {
        return;
      }

      // Gate all drag work behind the click threshold. Below it the press is
      // still a candidate click (modifier-click opens the part menu), so running
      // projection/snap/onDrag/setDragPosition would move the part and commit a
      // change on release from a few pixels of hand jitter. Once crossed, the
      // flag latches for the rest of the press so later sub-threshold moves
      // (including returning toward the start) keep updating normally.
      if (!pointerMovedRef.current) {
        return;
      }

      // Raycast pointer position onto drag plane
      const planePoint = raycastOntoPlane(
        event.clientX,
        event.clientY,
        dragPlane,
      );

      if (!planePoint) {
        return;
      }

      // Subtract offset to get the intended part center position (in World Space)
      const newWorldPosition = planePoint.clone().sub(offset);

      // Convert to Local Space (relative to parent, matching 'position' prop space)
      const newLocalPosition = newWorldPosition.clone();
      if (groupRef.current?.parent) {
        groupRef.current.parent.worldToLocal(newLocalPosition);
      }

      // Project onto constraint. Multi-area surfaces clamp to the nearest panel
      // (the part jumps across the gap); single-area/edge use the legacy path.
      const { constrainedPosition, activeCenter, activeAreaIndex } =
        projectConstrainedDragPosition(newLocalPosition, {
          constraintType: constraint.type,
          planeNormal,
          surfaceAreas,
          perAreaCenterSnap,
          defaultCenter: center,
          projectPoint,
        });
      if (activeAreaIndex !== null) {
        setActiveAreaIndex((prev) =>
          prev === activeAreaIndex ? prev : activeAreaIndex,
        );
      }

      const { finalPos, snapAnchor, surfaceSnap } = resolveDragSnapOutcome(
        constrainedPosition,
        {
          constraint,
          surfaceAxes,
          activeCenter,
          diagonalSnaps,
          threshold: SNAP_THRESHOLD,
          activeAreaIndex,
          surfaceAreas,
          planeNormal,
        },
      );
      if (surfaceSnap) {
        setSnappedAxes((prev) =>
          booleanArraysEqual(prev, surfaceSnap.axisSnapState)
            ? prev
            : surfaceSnap.axisSnapState,
        );
        setSnappedDiagonals((prev) =>
          booleanArraysEqual(prev, surfaceSnap.diagonalSnapState)
            ? prev
            : surfaceSnap.diagonalSnapState,
        );
      }

      const gridStepMm = dragGridStepFromModifiers(event.shiftKey);
      lastGridStepMmRef.current = gridStepMm;

      const dragUpdate = onDrag?.(finalPos, {
        gridStepMm,
      });
      const displayPos =
        trackLocalDragPosition && dragUpdate?.snappedPosition != null
          ? toVector3(dragUpdate.snappedPosition)
          : finalPos;

      lastDragFinalPosRef.current = displayPos.clone();

      // Controlled rebuild-driven parts (corner resizes) keep the built
      // `position` prop. Everything else mirrors locally — including legacy
      // uncontrolled usage, which always followed the cursor via finalPos.
      if (!controlled || trackLocalDragPosition) {
        setDragPosition(displayPos);
      }
      // Keep the session snapshot current so a remount mid-drag resumes from the
      // latest position/threshold state instead of the pointer-down values.
      persistDragSessionSnapshot(partId, displayPos, pointerMovedRef.current);
      setHoveredAnchor(snapAnchor);

      if (dragUpdate?.displayValues) {
        setDisplayValues(dragUpdate.displayValues);
      }
    },
    [
      projectPoint,
      constraint,
      planeNormal,
      surfaceAreas,
      surfaceAxes,
      perAreaCenterSnap,
      diagonalSnaps,
      center,
      onDrag,
      raycastOntoPlane,
      controlled,
      trackLocalDragPosition,
      partId,
      clearPendingReconcile,
    ],
  );

  // Keep hoveredAnchorRef in sync with state (for use in pointer up handler to avoid stale closure)
  useEffect(() => {
    hoveredAnchorRef.current = hoveredAnchor;
  }, [hoveredAnchor]);

  // Reconcile after a controlled drag ends: drop the optimistic `dragPosition`
  // only once an authoritative build commits a `position` that matches the
  // committed final drag target. Builds are coalesced/RAF-batched, so an older
  // in-flight drag build can land after pointer-up; clearing on the first prop
  // change (any build) would snap the part to that stale intermediate value and
  // cause a visible jump. Matching the final position swaps when the part is
  // already at the committed location -> no visible pop. A timeout fallback (set
  // in pointer-up) guards against a build that settles slightly off-target.
  useEffect(() => {
    if (!pendingReconcileRef.current || isDraggingRef.current) return;
    const finalPos = finalDragPositionRef.current;
    if (
      finalPos &&
      position.distanceToSquared(finalPos) > RECONCILE_MATCH_EPSILON_SQ
    ) {
      // Stale intermediate build (still chasing the final value): keep optimistic.
      return;
    }
    if (reconcileTimeoutRef.current !== null) {
      globalThis.clearTimeout(reconcileTimeoutRef.current);
      reconcileTimeoutRef.current = null;
    }
    pendingReconcileRef.current = false;
    finalDragPositionRef.current = null;
    setDragPosition(null);
  }, [position]);

  /**
   * Global pointer up handler for drag (attached to window during drag).
   * Uses refs instead of state to avoid stale closure issues.
   */
  const handleGlobalPointerUp = useCallback(
    (_event: PointerEvent) => {
      if (!isDraggingRef.current) {
        return;
      }

      isDraggingRef.current = false;

      // Treat a modifier-press that never moved past the threshold as a click,
      // not a drag, and open the part's menu instead of committing a position
      // change. Uses the per-frame movement flag (not down-vs-up distance) so a
      // drag that returns to its start still counts as a drag.
      const hadPointerDown = pointerDownScreenRef.current !== null;
      pointerDownScreenRef.current = null;
      const isClick = hadPointerDown && !pointerMovedRef.current;

      // End the module-level session FIRST to prevent any race conditions:
      // remove its window/canvas listeners and re-enable camera controls. This
      // runs correctly even if `this` is a stale (already unmounted) instance
      // whose drag was adopted-then-released during a remount gap.
      const session = activeDragSession;
      activeDragSession = null;
      if (session) {
        session.detach();
        session.cleanup?.();
      }
      cleanupRef.current = null;

      setIsDragging(false);
      endDrag(); // Clear global drag state (with delay for click suppression)
      setDisplayValues(undefined); // Clear display values

      // Use ref for hoveredAnchor to avoid stale closure
      const currentHoveredAnchor = hoveredAnchorRef.current;

      const committedPos = isClick
        ? null
        : (lastDragFinalPosRef.current?.clone() ?? null);
      if (committedPos) {
        onDragEnd?.(committedPos, currentHoveredAnchor, {
          gridStepMm: lastGridStepMmRef.current,
        });
      }

      setDragPosition((currentDragPos) => {
        // A press classified as a click (no movement past the threshold) must
        // not commit a position change — even when an earlier controlled drag's
        // optimistic dragPosition is still pending reconcile. Committing here
        // would fire a spurious onDragEnd settings update/rebuild while merely
        // opening the part menu. Leave the pending value untouched so its own
        // reconcile still resolves it.
        if (isClick) {
          return currentDragPos;
        }
        const optimisticPos = currentDragPos ?? committedPos;
        if (controlled && useOptimisticDrag && optimisticPos) {
          // Keep the optimistic position until an authoritative build matching
          // this committed value arrives, then reconcile (see effect above).
          // Clearing immediately would briefly show the previous built position
          // (one build behind) and cause a visible pop.
          pendingReconcileRef.current = true;
          finalDragPositionRef.current = optimisticPos.clone();
          if (reconcileTimeoutRef.current !== null) {
            globalThis.clearTimeout(reconcileTimeoutRef.current);
          }
          reconcileTimeoutRef.current = globalThis.setTimeout(() => {
            reconcileTimeoutRef.current = null;
            if (isDraggingRef.current) {
              return;
            }
            pendingReconcileRef.current = false;
            finalDragPositionRef.current = null;
            setDragPosition(null);
          }, RECONCILE_TIMEOUT_MS);
          return optimisticPos;
        }
        // Rebuild-driven (controlled edge) or legacy uncontrolled: dragPosition
        // is either unused or owned locally; clear so the built position wins.
        return null;
      });
      setHoveredAnchor(undefined);
      setSnappedAxes([]);
      setSnappedDiagonals([]);
      initialPositionRef.current = null;
      dragPlaneRef.current = null;
      offsetRef.current = null;
      lastDragFinalPosRef.current = null;

      // Reset didDragRef after a short delay to allow click event to be suppressed
      globalThis.setTimeout(() => {
        didDragRef.current = false;
      }, 100);
    },
    [onDragEnd, endDrag, controlled, useOptimisticDrag],
  );

  /**
   * Handle click - open the part's settings on a genuine (non-moving) click,
   * and suppress the click that concludes a drag.
   *
   * A plain left-click that did not travel opens the settings menu — no modifier
   * needed. `event.delta` is the pixel distance R3F measured between pointer-down
   * and this click, so a camera orbit (which also drives a click) is rejected by
   * the same threshold used for drag vs. click; an orbit usually also swings the
   * part out from under the cursor, so this handler never even fires. Modifier
   * clicks are handled by the pointer-up path instead (they set `didDragRef` on
   * pointer-down, so they are suppressed here) — the two paths never both fire.
   */
  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (didDragRef.current) {
        // Concludes a drag (or a modifier press): don't let it also toggle
        // something (e.g. the lid) or double-open via both paths.
        event.stopPropagation();
        return;
      }
      // Only claim the click when there is a menu to open, so parts without one
      // don't swallow clicks meant for handlers behind them.
      if (onActivate && event.delta <= CLICK_MOVE_THRESHOLD_PX) {
        event.stopPropagation();
        onActivate();
      }
    },
    [onActivate],
  );

  /**
   * Handle pointer down - start drag.
   * Requires Command key (⌘) on macOS or Ctrl key on Windows/Linux to be held
   * to prevent accidental drags while orbiting.
   */
  const handlePointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!enabled) return;

      const isLeftClick = event.nativeEvent.button === 0;
      const hasModifierKey =
        event.nativeEvent.metaKey || event.nativeEvent.ctrlKey;
      if (!isLeftClick || !hasModifierKey) return;
      if (isDraggingRef.current) return;

      // A prior drag can leave orphaned window listeners if pointer-up/cancel
      // was missed (tab switch, OS gesture). Overwriting the session without
      // detach leaks 6 listeners per drag and they keep firing on every move.
      if (activeDragSession) {
        teardownDragSession(activeDragSession);
        activeDragSession = null;
        endDrag();
      }

      event.stopPropagation();

      pointerDownScreenRef.current = {
        x: event.nativeEvent.clientX,
        y: event.nativeEvent.clientY,
      };
      pointerMovedRef.current = false;
      lastDragFinalPosRef.current = null;
      initialPositionRef.current = position.clone();

      // Get true world position for plane calculation
      const worldPosition = new Vector3();
      if (groupRef.current) {
        groupRef.current.getWorldPosition(worldPosition);
      } else {
        worldPosition.copy(position);
      }

      // Determine drag plane normal
      // For surfaces, drag along the surface plane for 1:1 movement
      // For edges or fallback, face the camera to ensure intersection
      let dragPlaneNormal: Vector3;
      if (constraint.type === "surface" && planeNormal) {
        dragPlaneNormal = planeNormal;
      } else {
        dragPlaneNormal = camera.position
          .clone()
          .sub(worldPosition)
          .normalize();
      }

      dragPlaneRef.current = new Plane().setFromNormalAndCoplanarPoint(
        dragPlaneNormal,
        worldPosition,
      );

      // Calculate offset: where on the plane we clicked relative to the part center (in world space)
      const clickPoint = raycastOntoPlane(
        event.nativeEvent.clientX,
        event.nativeEvent.clientY,
        dragPlaneRef.current,
      );

      if (clickPoint) {
        offsetRef.current = clickPoint.clone().sub(worldPosition);
      } else {
        offsetRef.current = new Vector3();
      }

      isDraggingRef.current = true;
      didDragRef.current = true; // Mark that a drag started (to suppress click)
      setIsDragging(true);
      startDrag(partId); // Track which part is dragging in global store

      // Attach the pointer listeners at module scope (not bound to this
      // component instance) so the drag survives an unmount/remount of this
      // part mid-drag. The listeners dispatch to the session's current handlers,
      // which the (re)mounted owner keeps pointed at its own latest closures.
      const detach = attachDragPointerListeners(gl.domElement);

      // Call onDragStart (disables camera controls); its cleanup is stored on
      // the session so it runs exactly once when the drag truly ends, even
      // across a mid-drag remount.
      const cleanup = onDragStart?.() ?? null;
      cleanupRef.current = cleanup;

      activeDragSession = {
        partId,
        handlers: {
          onMove: handleGlobalPointerMove,
          onUp: handleGlobalPointerUp,
        },
        snapshot: {
          plane: dragPlaneRef.current,
          offset: offsetRef.current,
          initialPosition: initialPositionRef.current,
          pointerDownScreen: pointerDownScreenRef.current,
          pointerMoved: pointerMovedRef.current,
          lastDragPosition: null,
        },
        cleanup,
        detach,
      };
    },
    [
      enabled,
      position,
      onDragStart,
      camera,
      raycastOntoPlane,
      gl.domElement,
      handleGlobalPointerMove,
      handleGlobalPointerUp,
      constraint.type,
      planeNormal,
      partId,
      startDrag,
      endDrag,
    ],
  );

  // Adopt an in-flight drag session on mount, and decide on unmount whether the
  // drag should survive (this part is being remounted mid-drag) or end.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally mount/unmount only; reading latest refs is desired
  useEffect(() => {
    // If this part already has a drag in flight, the previous instance was
    // unmounted mid-drag (transient rebuild). Resume from the snapshot instead
    // of dropping the drag.
    const session = activeDragSession;
    if (session && session.partId === partId) {
      const { snapshot } = session;
      isDraggingRef.current = true;
      didDragRef.current = true;
      dragPlaneRef.current = snapshot.plane;
      offsetRef.current = snapshot.offset;
      initialPositionRef.current = snapshot.initialPosition;
      pointerDownScreenRef.current = snapshot.pointerDownScreen;
      pointerMovedRef.current = snapshot.pointerMoved;
      cleanupRef.current = session.cleanup;
      // Take over the live listeners with this instance's handlers.
      session.handlers = {
        onMove: handleGlobalPointerMove,
        onUp: handleGlobalPointerUp,
      };
      setIsDragging(true);
      const resumeLocalDrag =
        !controlled ||
        constraint.optimistic === true ||
        constraint.snapDisplayDuringDrag === true;
      if (snapshot.lastDragPosition && resumeLocalDrag) {
        setDragPosition(snapshot.lastDragPosition.clone());
      }
    }

    return () => {
      // Mid-drag remount: keep the module session + its window listeners alive
      // so the remounted instance can adopt them. Re-enabling controls or
      // detaching here would abort the drag (the historic bug). The user's
      // pointer-up still ends the drag via the persistent listeners even if the
      // part is never remounted.
      if (isDraggingRef.current && activeDragSession?.partId === partId) {
        return;
      }
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      if (reconcileTimeoutRef.current !== null) {
        globalThis.clearTimeout(reconcileTimeoutRef.current);
        reconcileTimeoutRef.current = null;
      }
    };
  }, []); // Empty deps - mount/unmount only

  // Keep the live session pointed at this owner's latest handler closures so the
  // module-level listeners always dispatch to current state (handlers are
  // re-created when their dependencies change).
  useEffect(() => {
    if (activeDragSession?.partId === partId) {
      activeDragSession.handlers = {
        onMove: handleGlobalPointerMove,
        onUp: handleGlobalPointerUp,
      };
    }
  }, [handleGlobalPointerMove, handleGlobalPointerUp, partId]);

  // Hover tracks pure pointer presence — it must NOT be gated by drag state.
  // The drag runs on window listeners (no pointer capture), so R3F keeps
  // raycasting and fires leave the instant the moving mesh slips out from under
  // the cursor; gating leave on `!isDragging` dropped that event and left the
  // part stuck-hovered after the drag. No drag-time visual reads `isHovered`
  // (guides/anchors/outline key off `isDragging`; the glow is suppressed during
  // a drag), so updating it freely during a drag is safe and keeps it truthful.

  /**
   * Handle pointer enter - show hover state.
   */
  const handlePointerEnter = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!enabled) return;
      setIsHovered(true);
      // Seed the modifier state from the enter event: a key already held when
      // the pointer arrives won't re-fire keydown, so read it directly here.
      setModifierHeld(event.nativeEvent.metaKey || event.nativeEvent.ctrlKey);
    },
    [enabled],
  );

  /**
   * Handle pointer move - re-assert presence and re-sync the modifier. R3F only
   * fires `enter` on a boundary crossing, so this is what recovers a missed
   * enter/leave (fast drags, dynamic geometry) and a missed key event: any
   * motion over the part corrects both. Same-value setState is a no-op.
   */
  const handlePointerMove = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!enabled) return;
      setIsHovered(true);
      setModifierHeld(event.nativeEvent.metaKey || event.nativeEvent.ctrlKey);
    },
    [enabled],
  );

  /**
   * Handle pointer leave - hide hover state (always, even mid-drag).
   */
  const handlePointerLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // While hovered, track the drag modifier (⌘/Ctrl) for the stationary case
  // (press/release without moving). Motion-driven re-sync comes from
  // `handlePointerMove`; leaving the part tears this down and clears the flag.
  useEffect(() => {
    if (!isHovered) {
      setModifierHeld(false);
      return;
    }
    const sync = (e: KeyboardEvent) => setModifierHeld(e.metaKey || e.ctrlKey);
    const reset = () => setModifierHeld(false);
    globalThis.addEventListener("keydown", sync);
    globalThis.addEventListener("keyup", sync);
    globalThis.addEventListener("blur", reset); // focus lost mid-hold -> drop it
    return () => {
      globalThis.removeEventListener("keydown", sync);
      globalThis.removeEventListener("keyup", sync);
      globalThis.removeEventListener("blur", reset);
    };
  }, [isHovered]);

  // Activate-only parts open a popup on click but never move, so they show no
  // drag affordances (constraint outline, guides, arrows) — only the hover glow.
  const isActivateOnly = constraint.activateOnly === true;

  // Drag-mode hints: shown only when the part is hovered AND the drag modifier
  // (⌘/Ctrl) is held, with no other drag in progress. Gates the movement
  // outline and the direction-arrow cross — mirroring the real drag gate — so
  // hover alone stays uncluttered. Does NOT gate the hover glow.
  const showDragModeHint = isHovered && modifierHeld && !isDragActive;

  // Show visuals when dragging, in drag-mode hover, or in debug mode
  const showVisuals =
    !isActivateOnly && (isDragging || showDragModeHint || debug);

  // Affordance highlight: a soft glow around the part on hover so it reads as
  // movable. Shown on bare hover (no modifier needed); suppressed during an
  // active drag to avoid clutter.
  const showAffordance =
    enabled && showHoverHighlight && !isDragging && !isDragActive && isHovered;

  // Arrows hint the movement direction during this part's drag and in drag-mode
  // hover (modifier held).
  const showArrows =
    !isActivateOnly && (draggingPartId === partId || showDragModeHint);

  // Reinforce grabbability with the cursor: `grabbing` while actively dragging
  // this part, `move` while in drag-mode over it. Uses the single-owner cursor
  // manager so a hover hand-off between parts can't leave the shared canvas
  // cursor stuck. Releasing (leave / modifier release / drag end / unmount)
  // resets to the canvas default only while this part still owns the cursor.
  useEffect(() => {
    const draggingThis = draggingPartId === partId;
    let cursor: "grabbing" | "move" | null = null;
    if (draggingThis) {
      cursor = "grabbing";
    } else if (showDragModeHint) {
      cursor = "move";
    }
    if (!cursor) return;
    const el = gl.domElement;
    acquireCanvasCursor(el, partId, cursor);
    return () => releaseCanvasCursor(el, partId);
  }, [showDragModeHint, draggingPartId, partId, gl.domElement]);

  // Generate constraint visualization geometry
  const constraintLineGeometry = useMemo(() => {
    if (!showVisuals) return null;

    const geometry = new BufferGeometry();

    if (constraint.type === "edge") {
      // Line from min to max along axis
      const points = [
        bounds.min.x,
        bounds.min.y,
        bounds.min.z,
        bounds.max.x,
        bounds.max.y,
        bounds.max.z,
      ];
      geometry.setAttribute("position", new Float32BufferAttribute(points, 3));
    } else if (constraint.type === "surface" && planeNormal) {
      // One rectangle outline per valid panel (drag area), so the user sees all
      // drop zones and the gap between them. Falls back to the single bounds box
      // when the constraint has no separate areas.
      const boxes =
        surfaceAreas.length > 0
          ? surfaceAreas.map((area) => area.bounds)
          : [bounds];
      const points = boxes.flatMap((box) =>
        surfaceRectSegments(box.min, box.max, planeNormal),
      );
      geometry.setAttribute("position", new Float32BufferAttribute(points, 3));
    }

    return geometry;
  }, [showVisuals, constraint.type, bounds, planeNormal, surfaceAreas]);

  const constraintLineMaterial = useMemo(() => {
    let color: number;
    let opacity: number;

    if (isDragging) {
      color = 0x00ff88;
      opacity = 1;
    } else if (debug) {
      color = 0xff00ff;
      opacity = 0.8;
    } else {
      // Hover: a clearly visible accent outline so the movement area reads.
      color = 0x4aa3ff;
      opacity = 0.75;
    }

    return new LineBasicMaterial({
      color,
      opacity,
      transparent: true,
      linewidth: 2,
      depthTest: false,
    });
  }, [isDragging, debug]);

  useEffect(() => {
    return () => {
      constraintLineGeometry?.dispose();
    };
  }, [constraintLineGeometry]);

  useEffect(() => {
    return () => {
      constraintLineMaterial?.dispose();
    };
  }, [constraintLineMaterial]);

  // Calculate arrow directions based on constraint type
  const arrowDirections = useMemo(() => {
    const directions: Vector3[] = [];

    if (constraint.type === "edge" && constraint.axis) {
      // Edge constraint: show arrows along the axis (both directions)
      const axis = new Vector3(
        constraint.axis[0],
        constraint.axis[1],
        constraint.axis[2],
      ).normalize();
      directions.push(axis.clone());
      directions.push(axis.clone().negate());
    } else if (constraint.type === "surface" && planeNormal) {
      // Surface constraint: show arrows aligned with world axes that lie in the plane
      const normal = planeNormal.clone().normalize();
      const worldAxes = [
        new Vector3(1, 0, 0), // X
        new Vector3(0, 1, 0), // Y
        new Vector3(0, 0, 1), // Z
      ];

      for (const axis of worldAxes) {
        // Check if axis is parallel to normal (dot product close to ±1)
        const dot = Math.abs(normal.dot(axis));
        if (dot < 0.9) {
          // Project axis onto plane: axis - (axis · normal) * normal
          const projected = axis
            .clone()
            .sub(normal.clone().multiplyScalar(axis.dot(normal)))
            .normalize();
          directions.push(projected);
          directions.push(projected.clone().negate());
        }
      }
    }

    return directions;
  }, [constraint.type, constraint.axis, planeNormal]);

  // Compute current display position (drag position or original)
  const displayPosition = dragPosition || position;

  return (
    <group>
      {/* The draggable part */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: Three.js group element, not DOM */}
      <group
        ref={groupRef}
        position={[displayPosition.x, displayPosition.y, displayPosition.z]}
        rotation={rotation ? [rotation.x, rotation.y, rotation.z] : undefined}
        onPointerDown={enabled ? handlePointerDown : undefined}
        onPointerEnter={
          enabled && showHoverHighlight ? handlePointerEnter : undefined
        }
        onPointerMove={
          enabled && showHoverHighlight ? handlePointerMove : undefined
        }
        onPointerLeave={enabled ? handlePointerLeave : undefined}
        onClick={handleClick}
      >
        {children}
      </group>

      {/* Direction arrows - rendered at part position but WITHOUT rotation (world-aligned) */}
      {showArrows && (
        <group
          position={[displayPosition.x, displayPosition.y, displayPosition.z]}
          {...({ userData: { isDragHelper: true } } as WithUserData)}
        >
          {arrowDirections.map((direction) => {
            // Create a stable key from direction components
            const dirKey = `${direction.x.toFixed(2)}_${direction.y.toFixed(2)}_${direction.z.toFixed(2)}`;
            return <Arrow3D key={`arrow-${dirKey}`} direction={direction} />;
          })}
        </group>
      )}

      {/* Display values during drag */}
      {displayValues && (
        <Html
          position={[
            displayPosition.x,
            displayPosition.y + 80,
            displayPosition.z,
          ]}
          center
          style={{
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          <div
            style={{
              background: "rgba(0, 0, 0, 0.8)",
              color: "#fff",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              fontFamily: "monospace",
              whiteSpace: "nowrap",
              display: "flex",
              gap: "8px",
            }}
          >
            {displayValues.primary && (
              <span>
                {displayValues.primary.label}: {displayValues.primary.value}
                {displayValues.primary.unit}
              </span>
            )}
            {displayValues.secondary && (
              <span>
                {displayValues.secondary.label}: {displayValues.secondary.value}
                {displayValues.secondary.unit}
              </span>
            )}
          </div>
        </Html>
      )}

      {/* Constraint visualization (on hover, during drag, or debug) */}
      {showVisuals && constraintLineGeometry && (
        <lineSegments
          geometry={constraintLineGeometry}
          material={constraintLineMaterial}
          renderOrder={999}
          {...({ userData: { isDragHelper: true } } as WithUserData)}
        />
      )}

      {/* Center guide lines (+ diagonal snap lines) for surface drags.
          Per-area mode draws one center cross per panel; global mode draws a
          single cross across the whole side. Diagonal snaps stay global. */}
      {(isDragging || debug) && perAreaCenterSnap && (
        <>
          {areaGuides.map((guide) => (
            <DragGuideLines
              key={guide.key}
              center={guide.center}
              axes={guide.axes}
            />
          ))}
          {guideSegments.length > 0 && (
            <DragGuideLines
              center={center}
              axes={[]}
              segments={guideSegments}
            />
          )}
        </>
      )}
      {(isDragging || debug) &&
        !perAreaCenterSnap &&
        (guideAxes.length > 0 || guideSegments.length > 0) && (
          <DragGuideLines
            center={center}
            axes={guideAxes}
            segments={guideSegments}
          />
        )}

      {/* Debug: Show bounds corners */}
      {debug && (
        <group {...({ userData: { isDragHelper: true } } as WithUserData)}>
          {/* Min corner */}
          <mesh
            position={[bounds.min.x, bounds.min.y, bounds.min.z]}
            renderOrder={999}
          >
            <boxGeometry args={[5, 5, 5]} />
            <meshBasicMaterial
              color={0xff0000}
              opacity={0.8}
              transparent
              depthTest={false}
            />
          </mesh>
          {/* Max corner */}
          <mesh
            position={[bounds.max.x, bounds.max.y, bounds.max.z]}
            renderOrder={999}
          >
            <boxGeometry args={[5, 5, 5]} />
            <meshBasicMaterial
              color={0x00ff00}
              opacity={0.8}
              transparent
              depthTest={false}
            />
          </mesh>
          {/* Current position marker */}
          <mesh
            position={[position.x, position.y, position.z]}
            renderOrder={999}
          >
            <sphereGeometry args={[8, 16, 16]} />
            <meshBasicMaterial
              color={0x0000ff}
              opacity={0.5}
              transparent
              depthTest={false}
            />
          </mesh>
        </group>
      )}

      {/* Per-panel center snap points (Korpus): one marker per panel center,
          highlighted for the panel the part is currently over. */}
      {(isDragging || debug) &&
        perAreaCenterSnap &&
        areaGuides.map((guide, areaIdx) => {
          const isActive = areaIdx === activeAreaIndex;
          return (
            <mesh
              key={`area-center-${guide.key}`}
              position={[guide.center.x, guide.center.y, guide.center.z]}
              renderOrder={999}
              {...({ userData: { isDragHelper: true } } as WithUserData)}
            >
              <sphereGeometry args={[isActive ? 6 : 5, 16, 16]} />
              <meshBasicMaterial
                color={isActive ? 0x00ff88 : 0xffff00}
                opacity={isActive ? 1 : 0.8}
                transparent
                depthTest={false}
              />
            </mesh>
          );
        })}

      {/* Snap anchor points (during drag or debug) */}
      {(isDragging || debug) &&
        !perAreaCenterSnap &&
        constraint.snapAnchors.map((anchor) => {
          const anchorPos = toVector3(anchor.position);
          const anchorIsHovered = hoveredAnchor === anchor;
          return (
            <mesh
              key={`anchor-${anchor.position.join(",")}`}
              position={[anchorPos.x, anchorPos.y, anchorPos.z]}
              renderOrder={999}
              {...({ userData: { isDragHelper: true } } as WithUserData)}
            >
              <sphereGeometry args={[anchorIsHovered ? 8 : 5, 16, 16]} />
              <meshBasicMaterial
                color={anchorIsHovered ? 0x00ff00 : 0xffff00}
                opacity={anchorIsHovered ? 1 : 0.8}
                transparent
                depthTest={false}
              />
            </mesh>
          );
        })}

      {/* Debug: Show drag position if dragging */}
      {debug && dragPosition && (
        <mesh
          position={[dragPosition.x, dragPosition.y, dragPosition.z]}
          renderOrder={999}
          {...({ userData: { isDragHelper: true } } as WithUserData)}
        >
          <sphereGeometry args={[10, 16, 16]} />
          <meshBasicMaterial
            color={0x00ffff}
            opacity={0.8}
            transparent
            depthTest={false}
          />
        </mesh>
      )}

      {/* Affordance highlight: soft glow around the part on hover */}
      {showAffordance && (
        <DragHighlightGlow
          target={groupRef.current}
          refitKey={displayPosition}
        />
      )}
    </group>
  );
};

export default DraggablePart;
